import { useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';

import getPrintProduct from '../../graphql/queries/getPrintProduct';
import getPrintSizeCard from '../../graphql/queries/getPrintSizeCard';
import useAccountCardConfigure from '../../../cards/views/AccountCardConfigurator/useAccountCardConfigure';
import { CARD_ELEMENT_TYPE, ORIENTATION } from '../../../cards/constants';
import getGlobalPrintProductAlbumCustomPrint from '../../graphql/queries/getGlobalPrintProductAlbumCustomPrint';
import { getAutoFitPositionPhotoCropbox } from '../../../cards/components/CardElementPhoto/helpers';
import useTargetAccountCardInputFetch from '../../../cards/views/AccountCardConfigurator/useTargetAccountCardInputFetch';
import { isStandardPrint } from '../../../../util/print-product';
import { getMaxThumb } from '../../../../util/photo';

/**
 * PrintProduct configuration hook.
 *
 * @param {Object} options The options.
 * @param {string} options.printProductId The PrintProduct RecordID.
 * @param {string} [options.defaultPrintProductSizeId] A PrintProductSize RecordID as an optional default.
 * @param {string} [options.defaultPrintProductSizeServiceIds] The PrintProductSizeService RecordIDs as an optional default.
 * @param {Object} [options.defaultPhoto] A Photo type object
 * @param {string} [options.defaultAccountCardId] An Account Card ID as an optional default.
 * @param {string} [options.defaultOrientation] Default orientation.
 * @param {Object} [options.presetCardElementConfigurationInputs] Default card element configurations, mainly used for asset images..
 * @param {Boolean} [options.storeFetchedAccountCard] whether or not to store the fetched card by the useCardConfigure hook
 * @param {Object} [options.onError] Method to be called in the instance of an error.
 * @returns {{loadingPrintProduct: boolean, printProductConfiguration: {handlePrintProductSizeSelect: handlePrintProductSizeSelect, selectedPrintProductSize: undefined, selectedPrintProductMount: undefined, handlePrintProductMountSelect: handlePrintProductMountSelect}, accountCardConfiguration: {accountCard, onElementConfigurationChange: (function(*=, *=): Promise<void>), cardElementLoading: *, loadingAccountCard: (boolean|boolean)}, printProduct: *, globalPrintProductAlbumCustomPrint}}
 */
const usePrintProductConfiguration = ({
  printProductId,
  defaultPrintProductSizeId,
  defaultPrintProductSizeServiceIds,
  defaultPhoto,
  defaultAccountCardId,
  defaultOrientation,
  presetCardElementConfigurationInputs,
  storeFetchedAccountCard,
  onError,
}) => {
  const [selectedPrintProductSize, setSelectedPrintProductSize] = useState();
  const [selectedPrintProductMount, setSelectedPrintProductMount] = useState();
  const [
    selectedPrintProductSizeServices,
    setSelectedPrintProductSizeServices,
  ] = useState(defaultPrintProductSizeServiceIds ?? []);
  const [selectedCardSide, setSelectedCardSide] = useState();
  const [recalculateCropbox, setRecalculateCropbox] = useState(false);
  const { data: { globalAlbumCustomPrint } = {} } = useQuery(
    getGlobalPrintProductAlbumCustomPrint,
  );
  const { loading, data } = useQuery(getPrintProduct, {
    variables: {
      print_product_id: printProductId,
    },
    skip: !printProductId,
  });
  const [printProduct] = data?.printProducts?.records ?? [];
  const { loading: loadingCard, data: { card } = {} } = useQuery(
    getPrintSizeCard,
    {
      variables: {
        card_id: printProduct?.card?.id,
        print_size_id: selectedPrintProductSize?.print_size?.id,
        orientation: selectedPrintProductSize?.orientation,
      },
      skip: !printProduct?.card?.id || !selectedPrintProductSize,
    },
  );

  const {
    loading: loadingTargetAccountCardProduct,
    getTargetAccountCardInput,
  } = useTargetAccountCardInputFetch({ printProductId });

  const {
    onCardChange,
    accountCard,
    loadingAccountCard,
    onElementConfigurationChange,
    cardElementLoading,
  } = useAccountCardConfigure({
    accountCardId: defaultAccountCardId,
    presetCardElementConfigurationInputs: (() => {
      if (defaultAccountCardId) {
        return undefined;
      }

      if (defaultPhoto) {
        return [
          {
            cardElement: {
              type: CARD_ELEMENT_TYPE.PHOTO,
            },
            cardElementConfiguration: {
              photo_id: defaultPhoto.id,
            },
            calculateCropbox: (element, printSize) => {
              const canonicalWidth =
                defaultOrientation === ORIENTATION.LANDSCAPE
                  ? Math.max(printSize.width, printSize.height)
                  : Math.min(printSize.width, printSize.height);
              const canonicalHeight =
                defaultOrientation === ORIENTATION.LANDSCAPE
                  ? Math.min(printSize.width, printSize.height)
                  : Math.max(printSize.width, printSize.height);

              return getAutoFitPositionPhotoCropbox({
                cardElement: element.cardElement,
                cardWidth: canonicalWidth,
                cardHeight: canonicalHeight,
                thumb: getMaxThumb(defaultPhoto?.thumbnail_urls),
                faceboxes: (defaultPhoto?.faces?.records ?? []).map(
                  ({ facebox }) => facebox,
                ),
              });
            },
          },
        ];
      }

      return presetCardElementConfigurationInputs;
    })(),
    storeFetchedAccountCard,
    onError,
  });

  useEffect(() => {
    // Select the first CardSide so that there's always something rendered to the user.
    // When we add more CardSides the user will also be able to select their own (change template)
    // via a UI that renders the CardSides and their previews.
    if (card) {
      const [cardSideGroup] = card.card_side_groups.records ?? [];
      const [cardSide] = cardSideGroup.card_sides.records ?? [];
      setSelectedCardSide(cardSide);
    }
  }, [card]);

  useEffect(() => {
    if (
      card &&
      selectedCardSide &&
      !defaultAccountCardId &&
      !loadingTargetAccountCardProduct
    ) {
      // If a defaultAccountCardId is provided, we're likely
      // updating, as such, we don't need to call this method.
      onCardChange(
        {
          card_id: card?.id,
          front_card_side_id: selectedCardSide.id,
          print_product_configuration: {
            print_product_id: printProduct.id,
            print_product_size_id: selectedPrintProductSize?.id,
            print_product_size_mount_id: selectedPrintProductMount?.id,
            print_product_size_service_ids: selectedPrintProductSizeServices,
          },
        },
        true,
        getTargetAccountCardInput({
          printSize: selectedPrintProductSize?.print_size,
          orientation: selectedPrintProductSize?.orientation,
        }),
      );
    }
  }, [card?.id, selectedCardSide?.id, loadingTargetAccountCardProduct]);

  useEffect(() => {
    const { print_size: printSize } = selectedPrintProductSize ?? {};
    if (isStandardPrint(printProduct) && printSize && recalculateCropbox) {
      const [accountCardSide] = accountCard?.account_card_sides ?? [];
      const {
        side,
        card_side: cardSide,
        card_element_configurations: cardElementConfigurations,
      } = accountCardSide ?? {};
      const { orientation } = cardSide?.card_template ?? {};
      const cardElement = cardSide?.card_template?.card_elements?.find(
        ({ type }) => type === CARD_ELEMENT_TYPE.PHOTO,
      );
      const cardElementConfiguration = (cardElementConfigurations ?? []).find(
        config => config.card_element.id === cardElement?.id,
      );
      const { photo } = cardElementConfiguration ?? {};

      if (side && cardElement && cardElementConfiguration && photo) {
        const canonicalWidth =
          orientation === ORIENTATION.LANDSCAPE
            ? Math.max(printSize.width, printSize.height)
            : Math.min(printSize.width, printSize.height);

        const canonicalHeight =
          orientation === ORIENTATION.LANDSCAPE
            ? Math.min(printSize.width, printSize.height)
            : Math.max(printSize.width, printSize.height);

        onElementConfigurationChange(
          {
            cardElement,
            cardElementConfiguration,
            side,
          },
          {
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth: canonicalWidth,
              cardHeight: canonicalHeight,
              thumb: getMaxThumb(photo.thumbnail_urls),
              faceboxes: (photo.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
            }),
          },
          accountCard,
        );
      }
      setRecalculateCropbox(false);
    }
  }, [accountCard]);

  const handlePrintProductSizeSelect = (
    newPrintProductSizeId,
    orientation = ORIENTATION.LANDSCAPE,
  ) => {
    const newPrintProductSize = printProduct?.sizes?.records.find(
      printProductSize => printProductSize.id === newPrintProductSizeId,
    );

    if (newPrintProductSize) {
      setSelectedPrintProductSize({
        ...newPrintProductSize,
        // Try to use the orientation requested given it is part of the printProductSize
        // orientations. Otherwise, fallback to the 1st orientation available.
        orientation: newPrintProductSize.orientations.includes(orientation)
          ? orientation
          : newPrintProductSize.orientations[0],
      });
    }
  };

  const handlePrintProductMountSelect = newPrintProductMountId => {
    const newPrintProductMount = selectedPrintProductSize?.mounts?.records?.find(
      printProductMount => printProductMount.id === newPrintProductMountId,
    );

    if (newPrintProductMount) {
      setSelectedPrintProductMount(prev => {
        // Deselect if already selected.
        if (prev?.id === newPrintProductMount.id) {
          return null;
        }

        return newPrintProductMount;
      });
    }
  };

  const handlePrintProductSizeServiceSelect = printProductSizeServiceId => {
    if (selectedPrintProductSizeServices.includes(printProductSizeServiceId)) {
      setSelectedPrintProductSizeServices(
        selectedPrintProductSizeServices.filter(
          id => id !== printProductSizeServiceId,
        ),
      );
    } else {
      setSelectedPrintProductSizeServices([
        ...selectedPrintProductSizeServices,
        printProductSizeServiceId,
      ]);
    }
  };

  const cardSides =
    card?.card_side_groups?.records?.[0]?.card_sides?.records ?? [];
  const handleCardSideSelect = newCardSideId => {
    const newCardSide = cardSides?.find(
      cardSide => cardSide.id === newCardSideId,
    );

    if (newCardSide) {
      setSelectedCardSide(newCardSide);
    }
  };

  // Update AccountCardPrintProductConfiguration on size or mount updates
  useEffect(() => {
    if (accountCard && selectedCardSide) {
      // get the card side that matches the selectedPrintProductSize.
      const [cardSideGroup] = accountCard.card.card_side_groups.records ?? [];
      const printSizeCardSide = (cardSideGroup.card_sides.records ?? []).find(
        side =>
          side.card_template?.print_size?.id ===
            selectedPrintProductSize?.print_size?.id &&
          side.card_template?.orientation ===
            selectedPrintProductSize?.orientation,
      );

      onCardChange({
        front_card_side_id: printSizeCardSide?.id,
        print_product_configuration: {
          print_product_id: printProduct.id,
          print_product_size_id: selectedPrintProductSize?.id ?? null,
          print_product_size_mount_id: selectedPrintProductMount?.id ?? null,
          print_product_size_service_ids: selectedPrintProductSizeServices,
        },
      });

      if (isStandardPrint(printProduct)) {
        setRecalculateCropbox(true);
      }
    }
  }, [
    selectedPrintProductSize,
    selectedPrintProductMount,
    selectedPrintProductSizeServices,
  ]);

  // Initialization: load the default PrintProductSize
  useEffect(() => {
    if (defaultPrintProductSizeId && printProduct) {
      handlePrintProductSizeSelect(
        defaultPrintProductSizeId,
        defaultOrientation,
      );
    }
  }, [printProduct]);

  return {
    globalPrintProductAlbumCustomPrint: globalAlbumCustomPrint,
    printProduct,
    loadingPrintProduct: loading,
    printProductConfiguration: {
      selectedPrintProductSize,
      handlePrintProductSizeSelect,
      selectedPrintProductMount,
      handlePrintProductMountSelect,
      selectedPrintProductSizeServices,
      handlePrintProductSizeServiceSelect,
      selectedCardSide,
      handleCardSideSelect,
    },
    accountCardConfiguration: {
      loadingAccountCard: loadingCard || loadingAccountCard,
      accountCard,
      cardSides,
      onElementConfigurationChange,
      cardElementLoading,
    },
  };
};

export default usePrintProductConfiguration;
